import { FC, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { BACKEND_URL } from '../config'
import { fetchAllData, fetchWithAuthMethod } from '../utils';


export const EditSensorTypes: FC = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [sensorTypes, setSensorTypes] = useState<any[]>([]);
  const [sensorTypeName, setSensorTypeName] = useState('');
  const [sensorTypeDescription, setSensorTypeDescription] = useState('');
  const [sensorTypeCalibrationFactor, setSensorTypeCalibrationFactor] = useState('');
  const [sensorTypeUnit, setSensorTypeUnit] = useState('');
  const [sensorTypeDecimalPlaces, setSensorTypeDecimalPlaces] = useState('');
  const [selectedSensorTypeId, setSelectedSensorTypeId] = useState<string | null>(null); 
  const [isLoading, setIsLoading] = useState(false);
  const [deletingSensorTypeId, setDeletingSensorTypeId] = useState(null);
  
  const fetchData = async () => {
    try {
      // センサ種別情報を取得
      const fetchData = async () => {
        const sensorTypesResponse = await fetchAllData(`${BACKEND_URL}/sensor_types`);
        setSensorTypes(sensorTypesResponse);
      };
      
      fetchData().catch(console.error);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = async (row: any) => {
    setSelectedSensorTypeId(row.id);
    setSensorTypeName(row.name);
    setSensorTypeDescription(row.description);
    setSensorTypeCalibrationFactor(row.calibration_factor);
    setSensorTypeUnit(row.unit);
    setSensorTypeDecimalPlaces(row.decimal_places);
    setOpenEditDialog(true); // 編集ダイアログを開く

    setTimeout(() => {
      document.getElementById('sensor-type-name-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  // 校正係数入力チェック
  const validateCalibrationFactor = (): boolean => {
    const factor = Number(sensorTypeCalibrationFactor);
    if (isNaN(factor) || factor < 0.001 || factor > 1000) {
      alert("校正係数は 0.001 以上 1000 以下で入力してください");
      return false;
    }
  
    return true;
  };
  
  const handleEditSensorType = async () => {
    // 入力チェック
    if (!validateCalibrationFactor()) return;

    // 確認メッセージを表示
    const confirmed = window.confirm('更新してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setIsLoading(true); // ローディング開始

    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_types/${selectedSensorTypeId}`, `PUT`, {
      name: sensorTypeName,
      description: sensorTypeDescription,
      unit: sensorTypeUnit,
      calibration_factor: sensorTypeCalibrationFactor,
      decimal_places: sensorTypeDecimalPlaces
    });

    if (response.ok) {
      // センサ種別更新が成功した場合はセンサ種別リストを更新
      const updatedSensorType = await response.json();
      // センサ種別更新
      setSensorTypes(prevSensorTypes => {
        const newSensorTypes = [...prevSensorTypes];
        const index = newSensorTypes.findIndex(sensor_type => sensor_type.id === selectedSensorTypeId);    
        if (0 <= index) {
          // 指定センサ種別のデータのみ更新
          newSensorTypes[index] = {
            ...updatedSensorType
          };
        }
        return newSensorTypes;
      });

      handleCloseDialog('edit'); // 編集ダイアログを閉じる
    } else {
      const data = await response.json();
      if (data.detail.code === "SENSOR_TYPE_NAME_EXISTS") {
        alert("指定されたセンサ種別名称は既に登録されています。");
      }

      console.error('Failed to update sensor_type');
    }

    setIsLoading(false); // ローディング終了
  };

  const handleCloseDialog = (dialogType: 'add' | 'edit', event?: React.SyntheticEvent<Element, Event> | {}, reason?: string) => {
    // 背景をクリックされるとダイアログの各値がクリアされてしまう対策
    if (reason && reason === 'backdropClick') {
      return;
    }
    
    setSensorTypeName('');
    setSensorTypeDescription('');
    setSensorTypeUnit('');
    setSensorTypeCalibrationFactor('');
    setSensorTypeDecimalPlaces('');
	
    if (dialogType === 'add') {
      setOpenAddDialog(false); // 追加ダイアログを閉じる
    } else if (dialogType === 'edit') {
      setOpenEditDialog(false); // 編集ダイアログを閉じる
    }
  };

  const handleDeleteClick = async (row: any) => {
    // 確認メッセージを表示
    const confirmed = window.confirm('削除してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setDeletingSensorTypeId(row.id);
    setIsLoading(true); // ローディング開始
     
    // センサ種別削除
    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_types/${row.id}`, `DELETE`);
    if (response.ok) {
      // センサ種別リスト更新
      setSensorTypes(sensorTypes.filter((sensor_type: any) => sensor_type.id !== row.id));
    } else {
      const data = await response.json();
      if (data.detail.code === "HAS_LINKED_CHANNELS") {
        alert("このセンサ種別が割り当てられている地点データが存在するため、削除できません。");
      }

      console.error('Failed to delete sensor_type');
    }

    setIsLoading(false); // ローディング終了
    setDeletingSensorTypeId(null);
  };
  
  const handleAddClick = () => {
    setSensorTypeDecimalPlaces('0');
    setOpenAddDialog(true);

    setTimeout(() => {
      document.getElementById('sensor-type-name-input')?.focus(); // IDを使ってフォーカス
    }, 0);
  };

  const handleAddSensorType = async () => {
    // 入力チェック
    if (!validateCalibrationFactor()) return;

    // 確認メッセージを表示
    const confirmed = window.confirm('追加してもよろしいですか？');
    if (!confirmed) return; // ユーザーがキャンセルした場合は処理しない

    setIsLoading(true); // ローディング開始

    // センサ種別の追加
    const response = await fetchWithAuthMethod(`${BACKEND_URL}/sensor_types`, `POST`, {
      name: sensorTypeName,
      description: sensorTypeDescription,
      calibration_factor: sensorTypeCalibrationFactor,
      unit: sensorTypeUnit,
      decimal_places: sensorTypeDecimalPlaces
    });

    if (response.ok) {
      // 新しく作成されたセンサ種別を取得
      const newSensorType = await response.json();
      setSensorTypes([...sensorTypes, newSensorType]); // センサ種別リストに新しいセンサ種別を追加する
	
      handleCloseDialog('add'); // 追加ダイアログを閉じる
    } else {
      const data = await response.json();
      if (data.detail.code === "SENSOR_TYPE_NAME_EXISTS") {
        alert("指定されたセンサ種別名称は既に登録されています。");
      }

      console.error('Failed to add sensor_type');
    }

    setIsLoading(false); // ローディング終了
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'センサ種別名称',
      editable: false,
      width: 160,
    },
    {
      field: 'description',
      headerName: '説明',
      editable: false,
      width: 160,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'calibration_factor',
      headerName: '校正係数',
      editable: false,
      width: 160,
      align: 'right',
    },
    {
      field: 'unit',
      headerName: '単位',
      editable: false,
      width: 160,
    },
    {
      field: 'decimal_places',
      headerName: '小数点有効桁数',
      editable: false,
      width: 160,
      align: 'right',
    },
    {
      field: 'edit',
      headerName: '編集',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="edit" onClick={() => handleEditClick(params.row)}>
          <EditIcon />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: '削除',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton aria-label="delete" onClick={() => handleDeleteClick(params.row)}>
            {deletingSensorTypeId === params.row.id ? (
              <CircularProgress size={24} />
            ) : (
              <DeleteIcon />
            )}
        </IconButton>
      ),
    }
  ];

  return (
    <Box sx={{ height: '80vh', width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 250px)' }} id="datalist-grid">
        <DataGrid
          rows={sensorTypes}
          columns={columns}
          pageSize={100} // 1ページの表示件数
          rowsPerPageOptions={[100]}
          disableSelectionOnClick
          experimentalFeatures={{ newEditingApi: true }}
          density="compact"
          sx={{ flexGrow: 1, overflowY: 'auto',
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .Mui-selected": {
              outline: "none",
            },
          }}
        />
      </Box>
      <IconButton aria-label="add" onClick={handleAddClick}>
        <AddIcon />
		<Typography variant="body1">追加</Typography>
      </IconButton>
      <Dialog open={openAddDialog} onClose={(event, reason) => handleCloseDialog('add', event || {}, reason)}>
        <DialogTitle>センサ種別の追加</DialogTitle>
        <DialogContent>
          <TextField
            id="sensor-type-name-input"
            autoFocus
            margin="dense"
            label="センサ種別名称"
            fullWidth
            value={sensorTypeName}
            onChange={(e) => setSensorTypeName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="説明"
            fullWidth
            value={sensorTypeDescription}
            onChange={(e) => setSensorTypeDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="校正係数"
            fullWidth
            value={sensorTypeCalibrationFactor}
            onChange={(e) => setSensorTypeCalibrationFactor(e.target.value)}
          />
          <TextField
            margin="dense"
            label="単位"
            fullWidth
            value={sensorTypeUnit}
            onChange={(e) => setSensorTypeUnit(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label="小数点有効桁数"
            fullWidth
            value={sensorTypeDecimalPlaces}
            onChange={(e) => setSensorTypeDecimalPlaces(e.target.value)}
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('add')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleAddSensorType}
              disabled={((!sensorTypeName || 0 >= sensorTypeName.length) || 
                (!sensorTypeCalibrationFactor || 0 >= sensorTypeCalibrationFactor.length))}
            >
              追加
            </Button>
          )}        
        </DialogActions>
      </Dialog>
      <Dialog open={openEditDialog} onClose={(event, reason) => handleCloseDialog('edit', event || {}, reason)}>
        <DialogTitle>センサ種別の編集</DialogTitle>
        <DialogContent>
          <TextField
            id="sensor-type-name-input"
            autoFocus
            margin="dense"
            label="センサ種別名称"
            fullWidth
            value={sensorTypeName}
            onChange={(e) => setSensorTypeName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="説明"
            fullWidth
            value={sensorTypeDescription}
            onChange={(e) => setSensorTypeDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="校正係数"
            fullWidth
            value={sensorTypeCalibrationFactor}
            onChange={(e) => setSensorTypeCalibrationFactor(e.target.value)}
          />
          <TextField
            margin="dense"
            label="単位"
            fullWidth
            value={sensorTypeUnit}
            onChange={(e) => setSensorTypeUnit(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label="小数点有効桁数"
            fullWidth
            value={sensorTypeDecimalPlaces}
            onChange={(e) => setSensorTypeDecimalPlaces(e.target.value)}
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={1}>1</MenuItem>
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={3}>3</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDialog('edit')}>キャンセル</Button>
          {isLoading ? (
            <CircularProgress /> // MUIのスピナーを表示
          ) : (
            <Button 
              onClick={handleEditSensorType}
              disabled={((!sensorTypeName || 0 >= sensorTypeName.length) || 
                (!sensorTypeCalibrationFactor || 0 >= sensorTypeCalibrationFactor.length))}
            >
              更新
            </Button>            
          )} 
        </DialogActions>
      </Dialog>
    </Box>
  );
}